import { followPreference } from '../settings/preferences.js';
import { PING_BARS, pingBars, pingQuality } from './ping-bars.js';
import { uiFormat, uiText } from './text.js';

import './ping-meter.css';

/** One measure every two seconds: enough to follow the line, nothing on the wire. */
const PING_EVERY_MS = 2000;
/** No answer by then and the line counts as lost until the next one. */
const PING_TIMEOUT_MS = 5000;

/**
 * The round trip to the server, as signal bars and milliseconds, under the
 * top buttons when the player turns it on in the settings. It measures with
 * Colyseus's own ping, which the server answers without any game code, and
 * only while shown.
 */
export class PingMeter {
  private readonly root: HTMLElement;
  private readonly bars: HTMLElement[] = [];
  private readonly label: HTMLElement;
  private readonly unfollow: () => void;
  private timer: number | undefined;
  private timeout: number | undefined;
  /** Counts the measures, so a late answer never overwrites a newer one. */
  private asked = 0;

  constructor(private readonly ping: (answer: (ms: number) => void) => void) {
    this.root = document.createElement('div');
    this.root.className = 'ping-meter';
    const bars = document.createElement('span');
    bars.className = 'ping-meter__bars';
    bars.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < PING_BARS; i += 1) {
      const bar = document.createElement('span');
      bar.className = 'ping-meter__bar';
      bar.style.height = `${String(4 + i * 3)}px`;
      bars.append(bar);
      this.bars.push(bar);
    }
    this.label = document.createElement('span');
    this.label.className = 'ping-meter__ms';
    this.root.append(bars, this.label);
    this.paint(undefined);
    document.body.append(this.root);
    this.unfollow = followPreference('ping', (enabled) => {
      this.root.hidden = !enabled;
      if (enabled) this.start();
      else this.stop();
    });
  }

  private start(): void {
    if (this.timer !== undefined) return;
    this.measure();
    this.timer = window.setInterval(() => {
      this.measure();
    }, PING_EVERY_MS);
  }

  private stop(): void {
    window.clearInterval(this.timer);
    window.clearTimeout(this.timeout);
    this.timer = undefined;
    this.timeout = undefined;
  }

  private measure(): void {
    const id = (this.asked += 1);
    window.clearTimeout(this.timeout);
    this.timeout = window.setTimeout(() => {
      if (id === this.asked) this.paint(undefined);
    }, PING_TIMEOUT_MS);
    try {
      this.ping((ms) => {
        if (id !== this.asked) return;
        window.clearTimeout(this.timeout);
        this.paint(ms);
      });
    } catch {
      // The socket is closing: the timeout marks the line lost.
    }
  }

  private paint(ms: number | undefined): void {
    const quality = pingQuality(ms);
    const lit = pingBars(quality);
    this.root.dataset.quality = quality;
    this.bars.forEach((bar, i) => {
      bar.classList.toggle('ping-meter__bar--lit', i < lit);
    });
    this.label.textContent = ms === undefined ? '-- ms' : `${String(ms)} ms`;
    this.root.title =
      ms === undefined ? uiText('Ping : pas de réponse') : uiFormat('Ping : {ms} ms', { ms });
  }

  destroy(): void {
    this.stop();
    this.unfollow();
    this.root.remove();
  }
}
